import { useState } from "react";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Globe, Clock, ArrowRight } from "lucide-react";
import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/motion";
import { api, formatApiErrorDetail } from "../lib/api";
import { COMPANY, PROJECT_TYPES } from "../lib/content";
import { Seo } from "../components/Seo";

const field =
  "w-full bg-transparent border-b border-gray-300 py-4 text-darkgray placeholder:text-darkgray/40 focus:border-gold focus:outline-none transition-colors duration-300 font-light";
const label = "block text-[0.68rem] uppercase tracking-[0.2em] text-darkgray/50 font-semibold mb-2";

const EMPTY = { name: "", email: "", phone: "", company: "", project_type: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — your message has been received. We'll be in touch shortly.");
      setForm(EMPTY);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Seo
        title="Contact Next Horizon Builders | Southern California Contractor"
        description="Contact Next Horizon Builders for general contracting, preconstruction, multifamily construction, project management, and design-build services in Southern California."
        path="/contact"
        image="/media/generated/contact-hero.jpg"
      />
      <PageHero
        eyebrow="Contact"
        titleLines={["Let's build", "your horizon."]}
        subtitle="Tell us about your project. Our team will respond within one business day."
        image="/media/generated/contact-hero.jpg"
        imageAlt="Completed Next Horizon Builders multifamily building in Southern California"
      />

      <section className="bg-white py-24 md:py-36">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-14 lg:gap-24">
          {/* Info */}
          <div className="lg:col-span-4">
            <p className="eyebrow mb-6">Get In Touch</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-navy tracking-tight leading-tight">
              Start a conversation.
            </h2>
            <div className="mt-12 space-y-8">
              <div className="flex items-start gap-4">
                <MapPin className="text-gold mt-1 shrink-0" size={22} />
                <div>
                  <div className={label}>Office</div>
                  <p className="text-darkgray font-light">{COMPANY.address}</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Phone className="text-gold mt-1 shrink-0" size={22} />
                <div>
                  <div className={label}>Phone</div>
                  <a href={COMPANY.phoneHref} className="text-darkgray font-light hover:text-gold transition-colors">{COMPANY.phone}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Mail className="text-gold mt-1 shrink-0" size={22} />
                <div>
                  <div className={label}>Email</div>
                  <a href={`mailto:${COMPANY.email}`} className="text-darkgray font-light hover:text-gold transition-colors">{COMPANY.email}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Globe className="text-gold mt-1 shrink-0" size={22} />
                <div>
                  <div className={label}>Website</div>
                  <a href={COMPANY.website} target="_blank" rel="noopener noreferrer" className="text-darkgray font-light hover:text-gold transition-colors">{COMPANY.websiteLabel}</a>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <Clock className="text-gold mt-1 shrink-0" size={22} />
                <div>
                  <div className={label}>Business Hours</div>
                  {COMPANY.hours.map((h) => (
                    <p key={h.days} className="text-darkgray font-light">{h.days} · {h.time}</p>
                  ))}
                </div>
              </div>
              <div className="flex items-start gap-4">
                <MapPin className="text-gold mt-1 shrink-0" size={22} />
                <div>
                  <div className={label}>Service Areas</div>
                  <p className="text-darkgray font-light">{COMPANY.serviceAreas.join(" · ")}</p>
                </div>
              </div>
            </div>
            <p className="mt-12 text-xs uppercase tracking-[0.18em] text-darkgray/40">{COMPANY.license}</p>
          </div>

          {/* Form */}
          <div className="lg:col-span-8">
            <Reveal>
              <form onSubmit={submit} data-testid="contact-form" className="bg-lightgray p-8 md:p-14">
                <div className="grid md:grid-cols-2 gap-x-10 gap-y-8">
                  <div>
                    <label className={label} htmlFor="c-name">Full Name *</label>
                    <input id="c-name" data-testid="contact-name" required value={form.name} onChange={set("name")} className={field} placeholder="Jane Cooper" />
                  </div>
                  <div>
                    <label className={label} htmlFor="c-email">Email *</label>
                    <input id="c-email" data-testid="contact-email" type="email" required value={form.email} onChange={set("email")} className={field} placeholder="jane@company.com" />
                  </div>
                  <div>
                    <label className={label} htmlFor="c-phone">Phone</label>
                    <input id="c-phone" data-testid="contact-phone" value={form.phone} onChange={set("phone")} className={field} placeholder="(000) 000-0000" />
                  </div>
                  <div>
                    <label className={label} htmlFor="c-company">Company</label>
                    <input id="c-company" data-testid="contact-company" value={form.company} onChange={set("company")} className={field} placeholder="Company / Organization" />
                  </div>
                  <div className="md:col-span-2">
                    <label className={label} htmlFor="c-type">Project Type</label>
                    <select id="c-type" data-testid="contact-project-type" value={form.project_type} onChange={set("project_type")} className={`${field} cursor-pointer`}>
                      <option value="">Select a project type</option>
                      {PROJECT_TYPES.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div className="md:col-span-2">
                    <label className={label} htmlFor="c-message">Message *</label>
                    <textarea id="c-message" data-testid="contact-message" required rows={4} value={form.message} onChange={set("message")} className={`${field} resize-none`} placeholder="Tell us about your project, timeline, and location." />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="group mt-10 inline-flex items-center gap-3 bg-navy text-white px-9 py-5 text-xs font-semibold uppercase tracking-[0.2em] hover:bg-gold transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Message"}
                  <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
